import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * DynamicPageStyleLoader - Loads page-specific CSS files from themes/pages/
 * 
 * This component listens to route changes and dynamically loads/unloads
 * page-specific CSS files created by PageStyleEditor.
 * 
 * CSS files are expected at: /themes/pages/theme-{pageName}.css
 * 
 * IMPORTANT: These styles are loaded LAST to override all other styles.
 * Uses MutationObserver to ensure styles stay at the end of <head>.
 */
const DynamicPageStyleLoader = () => {
  const location = useLocation();
  // eslint-disable-next-line no-unused-vars
  const [loadedStyles, setLoadedStyles] = useState(new Set());
  const observerRef = useRef(null);
  const isReorderingRef = useRef(false);

  // Get page name from current route
  const getPageName = (pathname) => {
    const segments = pathname.split('/').filter(Boolean);
    
    if (segments.length === 0) return 'home';
    
    // Map routes to page names
    const routeMap = {
      '': 'home',
      'products': 'products',
      'product': 'product-detail',
      'about': 'about',
      'contact': 'contact',
      'cart': 'cart',
      'checkout': 'checkout',
      'account': 'account',
      'admin': 'admin',
      'login': 'login',
      'register': 'register',
      'category': 'category',
      'search': 'search',
      'blog': 'blog',
      'profile': 'profile',
      'faq': 'faq',
      'privacy-policy': 'privacy-policy',
      'terms-of-service': 'terms-of-service',
      'return-policy': 'return-policy',
      'shipping-policy': 'shipping-policy',
      'user-dashboard': 'user-dashboard',
      'user-settings': 'user-settings',
      'test-dashboard': 'test-dashboard'
    };
    
    return routeMap[segments[0]] || segments[0];
  };

  // Ensure our page-style CSS files are always at the END of head
  const ensureStylesAtEnd = () => {
    if (isReorderingRef.current) return;
    
    const pageStyleLinks = document.querySelectorAll('link[data-page-style]');
    if (pageStyleLinks.length === 0) return;
    
    // Check if any page-style link is NOT at the end
    const headChildren = Array.from(document.head.children);
    const lastElements = headChildren.slice(-pageStyleLinks.length);
    
    let needsReorder = false;
    pageStyleLinks.forEach(link => {
      if (!lastElements.includes(link)) {
        needsReorder = true;
      }
    });
    
    if (needsReorder) {
      isReorderingRef.current = true;
      console.log('[DynamicPageStyleLoader] Reordering styles to end of head');
      
      // Move all page-style links to end of head (in order: global-header, global-footer, page)
      const sortedLinks = Array.from(pageStyleLinks).sort((a, b) => {
        const aName = a.getAttribute('data-page-style');
        const bName = b.getAttribute('data-page-style');
        if (aName === 'global-header') return -1;
        if (bName === 'global-header') return 1;
        if (aName === 'global-footer') return -1;
        if (bName === 'global-footer') return 1;
        return 0;
      });
      
      sortedLinks.forEach(link => {
        document.head.appendChild(link);
      });
      
      isReorderingRef.current = false;
    }
  };

  // Load a CSS file helper - appends to END of head for highest priority
  const loadCSSFile = (cssName, priority = false) => {
    const cssId = `page-style-${cssName}`;
    // Use API endpoint to bypass Nginx/Cloudflare cache
    const cssUrl = `/api/admin/theme-compiler/serve-css/${cssName}`;

    // Remove existing if present (to re-append at end)
    const existing = document.getElementById(cssId);
    if (existing) {
      existing.remove();
    }

    // Create new link element
    const linkElement = document.createElement('link');
    linkElement.id = cssId;
    linkElement.rel = 'stylesheet';
    linkElement.href = `${cssUrl}?v=${Date.now()}`; // Cache busting
    linkElement.setAttribute('data-page-style', cssName);
    
    // Higher priority for global header/footer
    if (priority) {
      linkElement.setAttribute('data-priority', 'high');
    }

    // Handle load success
    linkElement.onload = () => {
      console.log(`[DynamicPageStyleLoader] Loaded: ${cssUrl}`);
      setLoadedStyles(prev => new Set([...prev, cssName]));
      // Ensure styles are at end after load
      setTimeout(ensureStylesAtEnd, 50);
    };

    // Handle load error (file doesn't exist)
    linkElement.onerror = () => {
      // Silently fail - CSS file doesn't exist
      linkElement.remove();
    };

    // IMPORTANT: Append to END of head for highest CSS priority
    document.head.appendChild(linkElement);
  };

  // Setup MutationObserver to watch for new stylesheets being added
  useEffect(() => {
    observerRef.current = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if a new stylesheet was added (not our page-style ones)
          for (const node of mutation.addedNodes) {
            if (node.tagName === 'LINK' || node.tagName === 'STYLE') {
              if (!node.hasAttribute('data-page-style')) {
                // A new stylesheet was added, ensure our styles are at end
                setTimeout(ensureStylesAtEnd, 10);
                break;
              }
            }
          }
        }
      }
    });

    observerRef.current.observe(document.head, {
      childList: true,
      subtree: false
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    const pageName = getPageName(location.pathname);
    
    // Load styles immediately, then again after a delay to ensure they're at end
    const loadStyles = () => {
      // Always load global header and footer styles first (apply to all pages)
      loadCSSFile('global-header', true);
      loadCSSFile('global-footer', true);
      
      // Then load page-specific styles (will be at the very end of head)
      loadCSSFile(pageName);
    };

    // Load immediately
    loadStyles();
    
    // Also load after delays to catch late-loading stylesheets
    const timer1 = setTimeout(ensureStylesAtEnd, 200);
    const timer2 = setTimeout(ensureStylesAtEnd, 500);
    const timer3 = setTimeout(ensureStylesAtEnd, 1000);

    // Cleanup old page styles on unmount
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [location.pathname]);

  // Listen for CSS save events from PageStyleEditor
  useEffect(() => {
    const handlePageThemeSaved = (event) => {
      const { pageName } = event.detail;
      console.log(`[DynamicPageStyleLoader] Page theme saved: ${pageName}`);
      
      // Force reload the CSS file by removing and re-adding at end
      const cssId = `page-style-${pageName}`;
      const existingLink = document.getElementById(cssId);
      
      if (existingLink) {
        existingLink.remove();
      }
      
      // Create new link at end of head with strong cache-busting
      const linkElement = document.createElement('link');
      linkElement.id = cssId;
      linkElement.rel = 'stylesheet';
      // Use API endpoint to bypass cache + timestamp and random number for cache busting
      const cacheBuster = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      linkElement.href = `/api/admin/theme-compiler/serve-css/${pageName}?v=${cacheBuster}`;
      linkElement.setAttribute('data-page-style', pageName);
      
      linkElement.onload = () => {
        console.log(`[DynamicPageStyleLoader] Reloaded CSS: theme-${pageName}.css`);
        setTimeout(ensureStylesAtEnd, 50);
      };
      
      linkElement.onerror = () => {
        console.error(`[DynamicPageStyleLoader] Failed to reload: theme-${pageName}.css`);
      };
      
      document.head.appendChild(linkElement);
      
      // Also try to clear any service worker cache
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => {
            caches.open(name).then(cache => {
              cache.delete(`/themes/pages/theme-${pageName}.css`);
            });
          });
        });
      }
    };

    window.addEventListener('pageThemeSaved', handlePageThemeSaved);
    
    return () => {
      window.removeEventListener('pageThemeSaved', handlePageThemeSaved);
    };
  }, []);

  // This component doesn't render anything
  return null;
};

export default DynamicPageStyleLoader;
